import React from 'react';
import ReactDOM from 'react-dom/client';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import App from './components/App.jsx';
import Counter from './components/Counter.jsx';
import PostCard from './components/PostCard.jsx';
import NewsletterSignup from './components/NewsletterSignup.jsx';
import SearchBox from './components/SearchBox.jsx';
import FallingPlaceholders from './components/FallingPlaceholders.jsx';
import ContactHub from './components/ContactHub.jsx';
import PodcastHub from './components/PodcastHub.jsx';
import ShowsGrid from './components/ShowsGrid.jsx';
import ResourcesHub from './components/ResourcesHub.jsx';
import GalleryDump from './components/GalleryDump.jsx';
import GalleryPage from './components/GalleryPage.jsx';
import ShowGalleryCarousel from './components/ShowGalleryCarousel.jsx';
import Header from './components/Header.jsx';
import Footer from './components/Footer.jsx';
import ContemporaryHomepage from './components/ContemporaryHomepage.jsx';
import ThemeEnhancer from './components/ThemeEnhancer.jsx';
import AboutDirectors from './components/AboutDirectors.jsx';
import DirectorModal from './components/DirectorModal.jsx';
import NavigationDropdown from './components/NavigationDropdown.jsx';
import PremiumNav from './components/PremiumNav.jsx';
import LanguageSwitcher from './components/LanguageSwitcher.jsx';
import AgendaPage from './components/AgendaPage.jsx';
import FullscreenSlider from './components/FullscreenSlider.jsx';
import { showsService } from './utils/showsService.js';

const SLIDER_CONTROL_PAGE_URL = '/slider-control/';

const safeSliderUrl = (value = '') => {
    const raw = String(value || '').trim();
    if (!raw) return null;

    try {
        const parsed = new URL(raw, window.location.origin);
        if (!/^https?:$/.test(parsed.protocol)) return null;
        return parsed.href;
    } catch {
        return null;
    }
};

const parseLargestSrcsetCandidate = (imgNode) => {
    const srcset = String(imgNode?.getAttribute('srcset') || '').trim();
    if (!srcset) return '';

    const candidates = srcset.split(',').map((entry) => {
        const parts = entry.trim().split(/\s+/);
        const url = parts[0] || '';
        const descriptor = parts[1] || '';

        if (/\d+w$/i.test(descriptor)) {
            return { url, score: Number.parseInt(descriptor, 10) || 0 };
        }

        if (/\d+(?:\.\d+)?x$/i.test(descriptor)) {
            return { url, score: Math.round((Number.parseFloat(descriptor) || 1) * 1000) };
        }

        return { url, score: 1 };
    }).filter(candidate => candidate.url);

    if (!candidates.length) return '';
    candidates.sort((a, b) => b.score - a.score);
    return candidates[0].url;
};

const optimizeSliderImageUrl = (rawUrl = '') => {
    const normalized = safeSliderUrl(rawUrl);
    if (!normalized) return null;

    try {
        const parsed = new URL(normalized);
        const isGhostImage = parsed.pathname.includes('/content/images/');
        if (!isGhostImage) return parsed.href;

        const existingWidth = Number.parseInt(parsed.searchParams.get('w') || '0', 10);
        if (!Number.isFinite(existingWidth) || existingWidth < 2400) {
            parsed.searchParams.set('w', '2400');
        }

        const existingQuality = Number.parseInt(parsed.searchParams.get('q') || '0', 10);
        if (!Number.isFinite(existingQuality) || existingQuality < 90) {
            parsed.searchParams.set('q', '90');
        }

        if (!parsed.searchParams.get('format')) {
            parsed.searchParams.set('format', 'webp');
        }

        return parsed.href;
    } catch {
        return normalized;
    }
};

const normalizeSliderSlide = (raw = {}) => {
    const title = String(raw.title || '').trim();
    const excerpt = String(raw.excerpt || raw.subtitle || '').trim();
    const authors = String(raw.authors || '').trim();
    const url = safeSliderUrl(raw.url || '');
    const image = safeSliderUrl(raw.image || raw.featureImage || '');

    if (!title && !image) return null;

    return {
        title,
        excerpt,
        authors,
        url,
        image
    };
};

const parseCaptionParts = (text = '') => {
    const value = String(text || '').trim();
    if (!value) return { title: '', excerpt: '' };

    const separator = value.includes('|') ? '|' : ' - ';
    if (!value.includes(separator)) {
        return { title: value, excerpt: '' };
    }

    const parts = value.split(separator).map(part => part.trim()).filter(Boolean);
    return {
        title: parts[0] || '',
        excerpt: parts.slice(1).join(' ') || ''
    };
};

const parseSliderImagesFromContent = (documentNode) => {
    const imageNodes = Array.from(documentNode.querySelectorAll('.slider-control-content .gh-content img, .gh-content img'));
    if (!imageNodes.length) return [];

    const slides = imageNodes.map((img, index) => {
        const sourceCandidate = parseLargestSrcsetCandidate(img) || img.currentSrc || img.getAttribute('src') || img.dataset.src || '';
        const image = optimizeSliderImageUrl(sourceCandidate);
        if (!image) return null;

        const parentFigure = img.closest('figure');
        const caption = parentFigure?.querySelector('figcaption')?.textContent || '';
        const alt = img.getAttribute('alt') || '';
        const link = parentFigure?.querySelector('a[href]')?.getAttribute('href') || '';

        const parsedFromCaption = parseCaptionParts(caption || alt);
        const slide = normalizeSliderSlide({
            title: parsedFromCaption.title || alt || `Slide ${index + 1}`,
            excerpt: parsedFromCaption.excerpt,
            image,
            url: link
        });

        return slide;
    }).filter(Boolean);

    const seen = new Set();
    return slides.filter((slide) => {
        if (!slide.image || seen.has(slide.image)) return false;
        seen.add(slide.image);
        return true;
    });
};

const parseSliderControlJson = (documentNode) => {
    const scriptNode = documentNode.querySelector('#slider-control-config[type="application/json"]');
    const attrNode = documentNode.querySelector('[data-slider-control-config]');
    const codeNode = documentNode.querySelector('.slider-control-content pre code, .slider-control-content code.language-json, .gh-content pre code');

    const rawJson = (scriptNode?.textContent || attrNode?.textContent || codeNode?.textContent || '').trim();
    if (!rawJson) return [];

    try {
        const parsed = JSON.parse(rawJson);
        const slides = Array.isArray(parsed) ? parsed : (Array.isArray(parsed?.slides) ? parsed.slides : []);
        return slides.map(normalizeSliderSlide).filter(Boolean);
    } catch (error) {
        console.warn('Invalid slider control JSON:', error);
        return [];
    }
};

const parseSliderControlSlides = (documentNode) => {
    const imageSlides = parseSliderImagesFromContent(documentNode);
    if (imageSlides.length) {
        return imageSlides;
    }

    return parseSliderControlJson(documentNode);
};

const loadSliderControlSlides = async () => {
    try {
        const response = await fetch(SLIDER_CONTROL_PAGE_URL, { credentials: 'same-origin' });
        if (!response.ok) return [];

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        return parseSliderControlSlides(doc);
    } catch (error) {
        console.warn('Unable to load slider control page:', error);
        return [];
    }
};

const loadShowSlides = async () => {
    try {
        const shows = await showsService.getShows();
        if (!Array.isArray(shows) || !shows.length) return [];

        const getPosition = (show) => {
            const value = Number.parseInt(show?.position, 10);
            return Number.isFinite(value) ? value : null;
        };

        const getTimestamp = (show) => {
            const parsed = new Date(show?.publishedAt || show?.date || 0);
            return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
        };

        const withImages = shows.filter(show => show && show.featureImage);

        const desiredPositions = [1, 2, 3, 4, 5];
        const topPositioned = desiredPositions
            .map((position) => withImages
                .filter(show => getPosition(show) === position)
                .sort((a, b) => getTimestamp(b) - getTimestamp(a))[0] || null)
            .filter(Boolean);

        const usedIds = new Set(topPositioned.map(show => show.id));

        const latestFallback = withImages
            .filter(show => !usedIds.has(show.id))
            .sort((a, b) => getTimestamp(b) - getTimestamp(a));

        const selected = [...topPositioned, ...latestFallback].slice(0, 5);

        return selected.map((show) => normalizeSliderSlide({
            title: show.title,
            excerpt: show.excerpt,
            image: show.featureImage,
            url: show.url,
            authors: show.venue || ''
        })).filter(Boolean);
    } catch (error) {
        console.warn('Unable to load shows for slider fallback:', error);
        return [];
    }
};

// Initialize React when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    // Mount ThemeEnhancer globally (enhances existing HTML, doesn't replace it)
    const themeEnhancerMount = document.createElement('div');
    themeEnhancerMount.id = 'theme-enhancer-mount';
    document.body.appendChild(themeEnhancerMount);
    const enhancerRoot = ReactDOM.createRoot(themeEnhancerMount);
    enhancerRoot.render(<ThemeEnhancer />);
    
    // Find all elements with data-react attribute
    const reactContainers = document.querySelectorAll('[data-react-component]');
    
    reactContainers.forEach((container) => {
        const componentName = container.getAttribute('data-react-component');
        
        // Collect props from data attributes
        const props = {};
        Array.from(container.attributes).forEach(attr => {
            if (attr.name.startsWith('data-') && attr.name !== 'data-react-component') {
                // Convert data-site-url to siteUrl
                const propName = attr.name
                    .replace('data-', '')
                    .replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                props[propName] = attr.value;
            }
        });
        
        // Helpers shared by all branches
        const stripDiacritics = (s = '') => String(s).normalize('NFD').replace(/\p{Diacritic}/gu, '');
        const canonicalKey = (labelOrSlug = '') => {
            const base = stripDiacritics(String(labelOrSlug).toLowerCase()).trim();
            if (/^(home|inicio)$/.test(base)) return 'home';
            if (/^(about|sobre|sobre\snos)$/.test(base)) return 'about';
            if (/^(shows?|espetaculos?|espetáculos)$/.test(base) || /show/.test(base)) return 'shows';
            if (/^(listen|ouvir|podcasts?)$/.test(base)) return 'listen';
            if (/^(resources?|recursos)$/.test(base)) return 'resources';
            if (/^(contacts?|contactos?)$/.test(base)) return 'contacts';
            if (/^(arquivo\+?|archive)$/.test(base)) return 'archive';
            return base.replace(/\s+/g, '-');
        };
        const normalizePath = (u) => {
            try {
                const { pathname } = new URL(u, window.location.origin);
                const noLocale = pathname.replace(/^\/(en|pt)(?=\/|$)/i, '');
                let p = noLocale.replace(/\/$/, '');
                if (p === '') p = '/';
                return p.toLowerCase();
            } catch {
                return (u || '/').replace(/[?#].*$/, '').replace(/\/$/, '').toLowerCase() || '/';
            }
        };

        const finalizeNavigation = (items = []) => {
            const order = ['about', 'shows', 'listen', 'resources', 'contacts', 'archive'];
            const withKeys = items.map(it => ({
                ...it,
                _k: canonicalKey(it.slug || it.label),
                _n: normalizePath(it.url)
            }));
            const deduped = withKeys
                .filter((item, idx, arr) => arr.findIndex(i => i._k === item._k) === idx)
                .filter((item, idx, arr) => arr.findIndex(i => i._n === item._n) === idx)
                .map(({ _k, _n, ...rest }) => rest);
            deduped.sort((a, b) => {
                const ia = order.indexOf(canonicalKey(a.slug || a.label));
                const ib = order.indexOf(canonicalKey(b.slug || b.label));
                return (ia === -1 ? Number.MAX_SAFE_INTEGER : ia) - (ib === -1 ? Number.MAX_SAFE_INTEGER : ib);
            });
            return deduped;
        };

        // Parse navigation data if present; derive from local fallback if empty
        if (props.navigation) {
            try {
                props.navigation = JSON.parse(props.navigation);
                props.navigation = finalizeNavigation(props.navigation);
                console.log('Parsed navigation (finalized):', props.navigation);
            } catch (e) {
                console.warn('Failed to parse navigation data:', e);
                props.navigation = [];
            }
        }
        if (!props.navigation || props.navigation.length === 0) {
            // Helper to normalize URLs for dedupe (remove locale, query/hash, trailing slash)
            const normalize = (u) => {
                try {
                    const { pathname } = new URL(u, window.location.origin);
                    // strip locale prefix like /en/ or /pt/
                    const noLocale = pathname.replace(/^\/(en|pt)(?=\/|$)/i, '');
                    // ensure leading slash and single trailing slash (except root)
                    let p = noLocale.replace(/\/$/, '');
                    if (p === '') p = '/';
                    return p.toLowerCase();
                } catch {
                    return (u || '/').replace(/[?#].*$/, '').replace(/\/$/, '').toLowerCase() || '/';
                }
            };

            const isInsideReact = (el) => !!el.closest('.react-nav-container');
            const isControl = (el) => el.matches('[role="button"], button, [data-portal], .gh-search, .gh-burger, .gh-btn');

            // Canonical key for labels to dedupe semantic duplicates (e.g., Shows/Espetáculos)
            const canonicalKey = (labelOrSlug = '') => {
                const stripDiacritics = (s) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '');
                const base = stripDiacritics(String(labelOrSlug).toLowerCase()).trim();
                // map common synonyms
                if (/^(home|inicio)$/.test(base)) return 'home';
                if (/^(about|sobre|sobre\snos)$/.test(base)) return 'about';
                if (/^(shows?|espetaculos?|espetáculos)$/.test(base) || /show/.test(base)) return 'shows';
                if (/^(listen|ouvir|podcasts?)$/.test(base)) return 'listen';
                if (/^(resources?|recursos)$/.test(base)) return 'resources';
                if (/^(contacts?|contactos?)$/.test(base)) return 'contacts';
                if (/^(arquivo\+?|archive)$/.test(base)) return 'archive';
                return base.replace(/\s+/g, '-');
            };

            // Extract from the fallback UL inside this container first
            const localLinks = container.querySelectorAll('ul.nav a[href]');
            if (localLinks && localLinks.length) {
                props.navigation = Array.from(localLinks).map(link => {
                    // Clean label: remove trailing + or - and trim
                    let label = link.textContent.trim();
                    label = label.replace(/[\+\-]$/, '').trim();
                    
                    return {
                        label: label,
                        url: link.getAttribute('href'),
                        _n: normalize(link.getAttribute('href')),
                        _k: canonicalKey(label)
                    };
                })
                // unique by canonical key first, then by normalized path
                .filter((item, idx, arr) => arr.findIndex(i => i._k === item._k) === idx)
                .filter((item, idx, arr) => arr.findIndex(i => i._n === item._n) === idx)
                .map(({ _n, _k, ...rest }) => rest);
                console.log('Derived navigation from local fallback:', props.navigation);
                props.navigation = finalizeNavigation(props.navigation);
            } else {
                // Prefer header menu if present; otherwise fall back to command bar
                const headerLinks = Array.from(document.querySelectorAll('.gh-head-menu a[href]'))
                    .filter(a => !isInsideReact(a) && !isControl(a));
                const source = headerLinks.length ? headerLinks : Array.from(document.querySelectorAll('#gh-command-bar a.gh-command-bar-menu-link[href], #gh-command-bar a.gh-command-bar-sub-link[href]'));

                if (source.length) {
                    const blacklist = [/^#/, /portal/, /\b(sign\s?in|subscribe)\b/i, /search/];
                    let items = source
                        .filter(link => link.getAttribute('href'))
                        .filter(link => !blacklist.some(rx => rx.test(link.getAttribute('href'))))
                        .map(link => {
                            let label = link.textContent.trim();
                            label = label.replace(/[\+\-]$/, '').trim();
                            return {
                                label: label,
                                url: link.getAttribute('href'),
                                _n: normalize(link.getAttribute('href')),
                                _k: canonicalKey(label)
                            };
                        })
                        // Remove semantic duplicates by canonical key, then by normalized URL
                        .filter((item, idx, arr) => arr.findIndex(i => i._k === item._k) === idx)
                        .filter((item, idx, arr) => arr.findIndex(i => i._n === item._n) === idx)
                        .map(({ _n, _k, ...rest }) => rest);

                    // Enforce preferred ordering while preserving unknowns at the end
                    const order = ['about', 'shows', 'listen', 'resources', 'contacts', 'archive'];
                    const keyOf = (label) => {
                        const s = label.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
                        if (/^(about|sobre|sobre\snos)$/.test(s)) return 'about';
                        if (/^(shows?|espetaculos?|espetáculos)$/.test(s) || /show/.test(s)) return 'shows';
                        if (/^(listen|ouvir|podcasts?)$/.test(s)) return 'listen';
                        if (/^(resources?|recursos)$/.test(s)) return 'resources';
                        if (/^(contacts?|contactos?)$/.test(s)) return 'contacts';
                        if (/^(arquivo\+?|archive)$/.test(s)) return 'archive';
                        return null;
                    };

                    items.sort((a, b) => {
                        const ka = keyOf(a.label);
                        const kb = keyOf(b.label);
                        const ia = ka ? order.indexOf(ka) : Number.MAX_SAFE_INTEGER;
                        const ib = kb ? order.indexOf(kb) : Number.MAX_SAFE_INTEGER;
                        return ia - ib;
                    });

                    // Trim to a reasonable size
                    props.navigation = finalizeNavigation(items).slice(0, 7);
                    console.log(`Derived navigation from ${headerLinks.length ? 'header menu' : 'command bar'}:`, props.navigation);
                } else {
                    // Inject default skeleton navigation
                    props.navigation = [
                        { label: 'Sobre nós', url: '/about/' },
                        { label: 'Espetáculos', url: '/shows/' },
                        { label: 'Ouvir', url: '/podcasts/' },
                        { label: 'Recursos', url: '/resources/' },
                        { label: 'Contactos', url: '/contacts/' }
                    ];
                    console.log('Using injected default navigation skeleton:', props.navigation);
                }
            }
        }
        
        // Parse dropdown items data if present
        if (props.dropdownItems) {
            try {
                props.dropdownItems = JSON.parse(props.dropdownItems);
                console.log('Parsed dropdown items:', props.dropdownItems);
            } catch (e) {
                console.warn('Failed to parse dropdown items data:', e);
                props.dropdownItems = {};
            }
        }
        
        // Create React root and render
        const root = ReactDOM.createRoot(container);
        
        // Render the appropriate component based on name
        try {
            switch (componentName) {
                case 'App':
                    root.render(<App {...props} />);
                    break;
                case 'Counter':
                    root.render(<Counter {...props} />);
                    break;
                case 'PostCard':
                    root.render(<PostCard {...props} />);
                    break;
                case 'NewsletterSignup':
                    root.render(<NewsletterSignup {...props} />);
                    break;
                case 'SearchBox':
                    root.render(<SearchBox {...props} />);
                    break;
                case 'FallingPlaceholders':
                    root.render(<FallingPlaceholders {...props} />);
                    break;
                case 'ContactHub':
                    root.render(<ContactHub {...props} />);
                    break;
                case 'PodcastHub':
                    root.render(<PodcastHub {...props} />);
                    break;
                case 'ShowsGrid':
                    root.render(<ShowsGrid {...props} />);
                    break;
                case 'ResourcesHub':
                    root.render(<ResourcesHub {...props} />);
                    break;
                case 'GalleryDump':
                    root.render(<GalleryDump {...props} />);
                    break;
                case 'GalleryPage':
                    root.render(<ErrorBoundary><GalleryPage {...props} /></ErrorBoundary>);
                    break;
                case 'ShowGalleryCarousel':
                    root.render(<ErrorBoundary><ShowGalleryCarousel {...props} /></ErrorBoundary>);
                    break;
                case 'Header':
                    root.render(<Header {...props} />);
                    break;
                case 'Footer':
                    root.render(<Footer {...props} />);
                    break;
                case 'ContemporaryHomepage':
                    root.render(<ContemporaryHomepage {...props} />);
                    break;
                case 'ThemeEnhancer':
                    root.render(<ThemeEnhancer {...props} />);
                    break;
                case 'AboutDirectors':
                    root.render(<AboutDirectors {...props} />);
                    break;
                case 'DirectorModal':
                    root.render(<DirectorModal {...props} />);
                    break;
                case 'NavigationDropdown':
                    console.log('Rendering NavigationDropdown with props:', props);
                    root.render(<NavigationDropdown {...props} />);
                    // Only hide fallback when we actually have items rendered
                    if (props.navigation && props.navigation.length > 0) {
                        container.classList.add('react-mounted');
                    }
                    // Add Ghost loaded class to avoid opacity hiding
                    document.body.classList.add('is-dropdown-loaded');
                    break;
                case 'PremiumNav':
                    root.render(
                        <ErrorBoundary>
                            <PremiumNav {...props} />
                        </ErrorBoundary>
                    );
                    // Mark as mounted so the HBS fallback nav can be visually hidden
                    // (the :not(.react-mounted) selector in CSS handles this)
                    if (props.navigation && props.navigation.length > 0) {
                        container.classList.add('react-mounted');
                    }
                    break;
                case 'LanguageSwitcher':
                    console.log('Rendering LanguageSwitcher with props:', props);
                    root.render(<LanguageSwitcher {...props} />);
                    break;
                case 'AgendaPage':
                    root.render(<AgendaPage {...props} />);
                    break;
                case 'FullscreenSlider': {
                    const slideEls = Array.from(container.querySelectorAll('template.fss-slide-data'));
                    const slidePosts = slideEls.map(el => ({
                        title: el.dataset.title,
                        url: el.dataset.url,
                        image: el.dataset.image,
                        excerpt: el.dataset.excerpt,
                        authors: el.dataset.authors,
                    })).filter(slide => slide.title || slide.image);

                    const renderSlider = (slides) => {
                        root.render(<FullscreenSlider {...props} posts={slides.length ? slides : null} />);
                    };

                    if (slidePosts.length) {
                        renderSlider(slidePosts);
                        break;
                    }

                    loadShowSlides()
                        .then((showSlides) => {
                            if (Array.isArray(showSlides) && showSlides.length) {
                                renderSlider(showSlides);
                                return;
                            }

                            return loadSliderControlSlides().then((controlledSlides) => {
                                renderSlider(controlledSlides || []);
                            });
                        })
                        .catch(() => {
                            loadSliderControlSlides()
                                .then((controlledSlides) => {
                                    renderSlider(controlledSlides || []);
                                })
                                .catch(() => {
                                    renderSlider([]);
                                });
                        });
                    break;
                }
                // Add more component cases as needed
                default:
                    console.warn(`Unknown React component: ${componentName}`);
            }
        } catch (error) {
            console.error(`Error rendering ${componentName}:`, error);
        }
    });

    // Example: Mount a default app if there's a #react-root element
    const defaultRoot = document.getElementById('react-root');
    if (defaultRoot && !defaultRoot.hasAttribute('data-react-component')) {
        const root = ReactDOM.createRoot(defaultRoot);
        root.render(<App />);
    }
});
